import type { OnlineAnswer, OnlineMap, OnlinePlayer } from "odyssey-shared";
import { timeoutSignal } from "../../timeout-signal.ts";

/**
 * How a GM's page asks the gateway who is online (ADR-056's amendment of 2026-10-06, decisions 4 and 5): a GET of
 * `/moderation/online` carrying the page's current session token as `Authorization: Bearer`, read at the moment of
 * sending, and no cookie. An answer is read field by field, so a player row out of shape is dropped rather than
 * drawn. What the page cannot read is a failed read and never an answer: a fetch that rejects without a status (a
 * network fault, or a gateway from before the route answering a bare 404 with no CORS header, which a page on
 * another origin cannot see), a body that is no JSON, and an answer whose shape is out of place.
 */

/**
 * What came of one request: the answer; the session ended (401); the roles no longer grant the list (403); over a
 * limit, with how long the gateway asked the page to wait; a refusal the gateway explained; or a failure the page
 * could not read.
 */
export type OnlineReply =
  | { readonly kind: "ok"; readonly answer: OnlineAnswer }
  | { readonly kind: "session" }
  | { readonly kind: "forbidden" }
  | { readonly kind: "rate"; readonly retryAfterMs: number }
  | { readonly kind: "failed" }
  | { readonly kind: "unreadable" };

/**
 * One request: the search or null for everyone, and the cursor of the page before or null for the first.
 */
export type OnlineFetch = (query: string | null, after: string | null) => Promise<OnlineReply>;

/**
 * What the client runs with: the gateway's `/moderation/online` URL, the page's current session token (null when it
 * holds none), and the fetch, the page's own unless a test gives another.
 */
export interface OnlineClientOptions {
  readonly endpoint: string;
  readonly token: () => string | null;
  readonly fetch?: (input: string, init?: RequestInit) => Promise<Response>;
}

/**
 * How long a request may take before the page gives up on it.
 */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * How long the page waits after a 429 that names no wait.
 */
const RATE_WAIT_MS = 60_000;

/**
 * Whether a value is a plain object.
 *
 * @param value The value.
 * @returns True for an object that is not an array or null.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads one map of the answer against its shape.
 *
 * @param value The map as sent.
 * @returns The map, or null when a field is missing or of another type.
 */
function onlineMap(value: unknown): OnlineMap | null {
  if (!isRecord(value)) {
    return null;
  }
  const { id, name, group, count } = value;
  return typeof id === "string" && typeof name === "string" && (group === null || typeof group === "string") && typeof count === "number" ? { id, name, group, count } : null;
}

/**
 * Reads one player of the answer against its shape.
 *
 * @param value The player as sent.
 * @returns The player, or null when a field is missing or of another type.
 */
function onlinePlayer(value: unknown): OnlinePlayer | null {
  if (!isRecord(value)) {
    return null;
  }
  const { first, family, map, you, staff, badge, badgeHidden, actable, frozen, muted } = value;
  if (typeof first !== "string" || typeof family !== "string" || typeof map !== "string" || typeof badge !== "number") {
    return null;
  }
  if (typeof you !== "boolean" || typeof staff !== "boolean" || typeof badgeHidden !== "boolean" || typeof actable !== "boolean" || typeof frozen !== "boolean" || typeof muted !== "boolean") {
    return null;
  }
  return { first, family, map, you, staff, badge, badgeHidden, actable, frozen, muted };
}

/**
 * Reads an answer against its shape.
 *
 * @param body The parsed body.
 * @returns The answer, or null when its shape is out of place; a player row out of shape is dropped and the rest
 *   kept.
 */
function onlineAnswer(body: unknown): OnlineAnswer | null {
  if (!isRecord(body)) {
    return null;
  }
  const { own, maps, players, total, next } = body;
  if ((own !== null && typeof own !== "string") || !Array.isArray(maps) || !Array.isArray(players) || typeof total !== "number" || (next !== null && typeof next !== "string")) {
    return null;
  }
  const read = maps.map(onlineMap);
  if (read.some((map) => map === null)) {
    return null;
  }
  return { own, maps: read as OnlineMap[], players: players.map(onlinePlayer).filter((player): player is OnlinePlayer => player !== null), total, next };
}

/**
 * Builds the client.
 *
 * @param options The endpoint, the token and the fetch.
 * @returns The fetch the model asks with; it never rejects.
 */
export function onlineClient(options: OnlineClientOptions): OnlineFetch {
  const send = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));
  return async (query, after) => {
    const token = options.token();
    if (token === null || token === "") {
      return { kind: "session" };
    }
    const params = [...(query === null ? [] : [`q=${encodeURIComponent(query)}`]), ...(after === null ? [] : [`after=${encodeURIComponent(after)}`])];
    const limit = timeoutSignal(REQUEST_TIMEOUT_MS);
    try {
      const response = await send(params.length === 0 ? options.endpoint : `${options.endpoint}?${params.join("&")}`, { method: "GET", headers: { Authorization: `Bearer ${token}` }, credentials: "omit", signal: limit.signal });
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        return { kind: "unreadable" };
      }
      if (response.status === 200) {
        const answer = onlineAnswer(body);
        return answer === null ? { kind: "unreadable" } : { kind: "ok", answer };
      }
      if (!isRecord(body) || typeof body["error"] !== "string") {
        return { kind: "unreadable" };
      }
      if (response.status === 401) {
        return { kind: "session" };
      }
      if (response.status === 403) {
        return { kind: "forbidden" };
      }
      if (response.status === 429) {
        const seconds = Number(response.headers.get("Retry-After"));
        return { kind: "rate", retryAfterMs: Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : RATE_WAIT_MS };
      }
      return { kind: "failed" };
    } catch {
      return { kind: "unreadable" };
    } finally {
      limit.settle();
    }
  };
}
